// BcDataManagement.js
import React, { useState } from 'react';
import { 
    Card, 
    Tabs, 
    Button, 
    Table, 
    message, 
    Space, 
    Tag, 
    Row, 
    Col, 
    Alert,
    Statistic,
    Progress,
    Badge,
    Modal,
    Typography
} from 'antd';
import { 
    DatabaseOutlined, 
    CloudDownloadOutlined, 
    ShopOutlined, 
    ProductOutlined, 
    UserOutlined, 
    DollarOutlined, 
    InboxOutlined, 
    FileTextOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    LoadingOutlined,
    ReloadOutlined,
    WarningOutlined
} from '@ant-design/icons';
import { useAuthContext } from "../../components/AuthProvider";
import { useBusinessContext } from "../../components/BusinessContext";

const { Text, Paragraph } = Typography;

function BcDataManagement() {
    const { token } = useAuthContext();
    const { businessId, businessName } = useBusinessContext();
    const [activeTab, setActiveTab] = useState('products');
    const [loading, setLoading] = useState({});
    const [pullAllLoading, setPullAllLoading] = useState(false);
    const [salesHeadersLoading, setSalesHeadersLoading] = useState(false);
    const [data, setData] = useState({
        products: [],
        shops: [],
        customers: [],
        itemPrices: [],
        inventories: [],
        salesHeaders: []
    });
    const [progress, setProgress] = useState({});
    const [responseMessageModal, setResponseMessageModal] = useState({
        visible: false,
        title: '',
        content: '',
        type: 'success'
    });

    // API endpoints in the exact order specified
    const endpoints = [
        { key: 'products', label: 'Products', icon: <ProductOutlined />, url: `/api/v1/bcintegration/business/${businessId}/fetchproducts` },
        { key: 'shops', label: 'Shops', icon: <ShopOutlined />, url: `/api/v1/bcintegration/business/${businessId}/fetchshops` },
        { key: 'customers', label: 'Customers', icon: <UserOutlined />, url: `/api/v1/bcintegration/business/${businessId}/fetchcustomers` },
        { key: 'itemPrices', label: 'Item Prices', icon: <DollarOutlined />, url: `/api/v1/bcintegration/business/${businessId}/fetchitemprices` },
        { key: 'inventories', label: 'Inventories', icon: <InboxOutlined />, url: `/api/v1/bcintegration/business/${businessId}/fetchinventories` }
    ];

    const fetchData = async (type, url, showMessage = true) => {
        if (!businessId) {
            message.warning('No business selected');
            return;
        }

        setLoading(prev => ({ ...prev, [type]: true }));
        
        if (showMessage) {
            message.loading({ content: `Fetching ${type}...`, key: type, duration: 0 });
        }

        try {
            const response = await fetch(`https://api.synergytoss.net${url}`, {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            const responseText = await response.text();
            let result;
            
            // Try to parse as JSON, if fails, treat as string message
            try {
                result = JSON.parse(responseText);
            } catch {
                result = responseText;
            }

            if (!response.ok) {
                // Extract just the response message without URL
                let errorMessage = typeof result === 'string' ? result : (result.message || result.error || 'Request failed');
                
                // Show error message in modal
                setResponseMessageModal({
                    visible: true,
                    title: `${type}`,
                    content: errorMessage,
                    type: 'error'
                });
                
                if (showMessage) {
                    message.error({ 
                        content: errorMessage.length > 100 ? errorMessage.substring(0, 100) + '...' : errorMessage, 
                        key: type, 
                        duration: 5 
                    });
                }
                return [];
            }

            // Handle different response types
            let fetchedData = [];
            let responseMessage = '';
            
            if (typeof result === 'string') {
                // Response is a string message
                responseMessage = result;
                fetchedData = [];
                
                // Show success message in modal
                setResponseMessageModal({
                    visible: true,
                    title: `${type}`,
                    content: result,
                    type: 'info'
                });
            } else if (Array.isArray(result)) {
                fetchedData = result;
                responseMessage = `Successfully fetched ${result.length} ${type}`;
            } else if (result.data && Array.isArray(result.data)) {
                fetchedData = result.data;
                responseMessage = result.message || `Successfully fetched ${result.data.length} ${type}`;
            } else if (result.message) {
                responseMessage = result.message;
                fetchedData = result.data || [];
                
                // Show message in modal if it's a string response
                if (typeof result.message === 'string' && result.message.length > 0 && (!fetchedData || fetchedData.length === 0)) {
                    setResponseMessageModal({
                        visible: true,
                        title: `${type}`,
                        content: result.message,
                        type: 'info'
                    });
                }
            } else {
                fetchedData = [];
                responseMessage = `No data found for ${type}`;
            }
            
            setData(prev => ({
                ...prev,
                [type]: fetchedData
            }));

            if (showMessage) {
                if (fetchedData.length > 0) {
                    message.success({ 
                        content: `Successfully fetched ${fetchedData.length} ${type}`, 
                        key: type, 
                        duration: 3 
                    });
                } else if (responseMessage && responseMessage !== `No data found for ${type}` && !responseMessage.includes('Successfully fetched')) {
                    message.info({ 
                        content: responseMessage.length > 100 ? responseMessage.substring(0, 100) + '...' : responseMessage, 
                        key: type, 
                        duration: 4 
                    });
                } else if (fetchedData.length === 0) {
                    message.info({ 
                        content: `No ${type} data available`, 
                        key: type, 
                        duration: 3 
                    });
                }
            }

            return fetchedData;
        } catch (error) {
            console.error(`Error fetching ${type}:`, error);
            const errorMessage = error.message || 'An unknown error occurred';
            
            if (showMessage) {
                message.error({ 
                    content: errorMessage.length > 100 ? errorMessage.substring(0, 100) + '...' : errorMessage, 
                    key: type, 
                    duration: 3 
                });
            }
            
            setResponseMessageModal({
                visible: true,
                title: `${type}`,
                content: errorMessage,
                type: 'error'
            });
            
            return [];
        } finally {
            setLoading(prev => ({ ...prev, [type]: false }));
        }
    };

    // Separate function for fetching Sales Headers
  const fetchSalesHeaders = async () => {
    if (!businessId) {
        message.warning('No business selected');
        return;
    }

    setSalesHeadersLoading(true);
    const type = 'Sales Headers';
    const url = `/api/v1/bcintegration/business/${businessId}/postsalesheaders`;
    
    message.loading({ content: 'Fetching Sales Headers...', key: 'salesHeaders', duration: 0 });

    try {
        const response = await fetch(`https://api.synergytoss.net${url}`, {
            method: 'GET',
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });

        // Check if response is successful (status code 200-299)
        if (response.ok) {
            // Try to get response body if it exists, but it might be empty
            const responseText = await response.text();
            let result = null;
            
            // Try to parse as JSON only if there's content
            if (responseText && responseText.trim()) {
                try {
                    result = JSON.parse(responseText);
                } catch {
                    // If parsing fails, use the text as result
                    result = responseText;
                }
            }
            
            // Handle successful response (with or without data)
            let fetchedData = [];
            let responseMessage = '';
            
            // Check if result is an array or contains data
            if (result && Array.isArray(result)) {
                fetchedData = result;
                responseMessage = `Successfully fetched ${result.length} Sales Headers`;
            } else if (result && result.data && Array.isArray(result.data)) {
                fetchedData = result.data;
                responseMessage = result.message || `Successfully fetched ${result.data.length} Sales Headers`;
            } else if (result && result.message) {
                responseMessage = result.message;
                fetchedData = result.data || [];
            } else if (!responseText || !responseText.trim()) {
                // Empty response - successful but no data
                responseMessage = 'Sales Headers request completed successfully';
                fetchedData = [];
            } else {
                fetchedData = [];
                responseMessage = 'Sales Headers request completed successfully';
            }
            
            setData(prev => ({
                ...prev,
                salesHeaders: fetchedData
            }));

            // Show success message based on data availability
            if (fetchedData.length > 0) {
                message.success({ 
                    content: `Successfully fetched ${fetchedData.length} Sales Headers`, 
                    key: 'salesHeaders', 
                    duration: 3 
                });
                
                // Show sales headers in a modal for better viewing
                Modal.info({
                    title: 'Sales Headers Data',
                    width: 800,
                    content: (
                        <Table
                            columns={getColumns('salesHeaders')}
                            dataSource={fetchedData}
                            rowKey={(record) => record.id || record.invoiceNo || Math.random()}
                            pagination={{ pageSize: 5 }}
                            size="small"
                            scroll={{ x: true }}
                        />
                    ),
                    okText: 'Close'
                });
            } else {
                // Success but no data
                message.success({ 
                    content: responseMessage || 'Sales Headers request completed successfully (no data available)', 
                    key: 'salesHeaders', 
                    duration: 3 
                });
            }
            
            return; // Exit early since request was successful
        }
        
        // Handle error responses (status code not in 200-299)
        const responseText = await response.text();
        let result;
        
        // Try to parse as JSON, if fails, treat as string message
        try {
            result = JSON.parse(responseText);
        } catch {
            result = responseText;
        }

        let errorMessage = typeof result === 'string' ? result : (result.message || result.error || 'Request failed');
        
        setResponseMessageModal({
            visible: true,
            title: 'Sales Headers',
            content: errorMessage,
            type: 'error'
        });
        
        message.error({ 
            content: errorMessage.length > 100 ? errorMessage.substring(0, 100) + '...' : errorMessage, 
            key: 'salesHeaders', 
            duration: 5 
        });
        
    } catch (error) {
        console.error('Error Posting Sales Headers:', error);
        const errorMessage = error.message || 'An unknown error occurred';
        
        message.error({ 
            content: errorMessage.length > 100 ? errorMessage.substring(0, 100) + '...' : errorMessage, 
            key: 'salesHeaders', 
            duration: 3 
        });
        
        setResponseMessageModal({
            visible: true,
            title: 'Sales Headers',
            content: errorMessage,
            type: 'error'
        });
    } finally {
        setSalesHeadersLoading(false);
    }
};

    const pullAllData = async () => {
        if (!businessId) {
            message.warning('No business selected');
            return;
        }

        setPullAllLoading(true);
        
        message.loading({ content: 'Pulling all data...', key: 'pullAll', duration: 0 });
        
        let successCount = 0;
        let failCount = 0;
        const results = [];

        // Fetch in the exact order specified (excluding sales headers)
        for (let i = 0; i < endpoints.length; i++) {
            const { key, label, url } = endpoints[i];
            
            setProgress(prev => ({ ...prev, [key]: 'loading' }));
            
            const result = await fetchData(key, url, false);
            
            if (result && result.length > 0) {
                setProgress(prev => ({ ...prev, [key]: 'success' }));
                successCount++;
                results.push({ type: label, count: result.length, status: 'success' });
            } else {
                setProgress(prev => ({ ...prev, [key]: 'error' }));
                failCount++;
                results.push({ type: label, count: 0, status: 'error' });
            }
        }

        setPullAllLoading(false);
        
        if (failCount === 0) {
            message.success({ 
                content: `Successfully pulled all data! (${successCount}/${endpoints.length} types)`, 
                key: 'pullAll', 
                duration: 5 
            });
        } else {
            message.warning({ 
                content: `Pull completed: ${successCount} successful, ${failCount} failed`, 
                key: 'pullAll', 
                duration: 5 
            });
        }
        
        console.log('Pull all results:', results);
        setTimeout(() => setProgress({}), 5000);
    };

    // Table columns for different data types
    const getColumns = (type) => {
        switch(type) {
            case 'products':
                return [
                    { title: 'Product Code', dataIndex: 'productCode', key: 'productCode' },
                    { title: 'Product Name', dataIndex: 'name', key: 'name' },
                    { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true },
                    { title: 'Category', dataIndex: 'category', key: 'category' },
                    { title: 'Unit Price', dataIndex: 'unitPrice', key: 'unitPrice', render: (price) => price ? `$${price}` : '-' },
                    { title: 'Status', dataIndex: 'active', key: 'active', render: (active) => <Tag color={active !== false ? 'green' : 'red'}>{active !== false ? 'Active' : 'Inactive'}</Tag> },
                ];
            case 'shops':
                return [
                    { title: 'Shop Code', dataIndex: 'shopCode', key: 'shopCode' },
                    { title: 'Shop Name', dataIndex: 'name', key: 'name', render: (text, record) => text || record.shopName },
                    { title: 'Phone', dataIndex: 'telephone', key: 'telephone' },
                    { title: 'Email', dataIndex: 'email', key: 'email' },
                    { title: 'Location', dataIndex: 'locationAddress', key: 'locationAddress', ellipsis: true },
                    { title: 'Status', dataIndex: 'active', key: 'active', render: (active) => <Tag color={active !== false ? 'green' : 'red'}>{active !== false ? 'Active' : 'Inactive'}</Tag> },
                ];
            case 'customers':
                return [
                    { title: 'Customer Code', dataIndex: 'customerCode', key: 'customerCode' },
                    { title: 'Name', dataIndex: 'name', key: 'name' },
                    { title: 'Phone', dataIndex: 'telephone', key: 'telephone' },
                    { title: 'Email', dataIndex: 'email', key: 'email' },
                    { title: 'Address', dataIndex: 'address', key: 'address', ellipsis: true },
                    { title: 'Status', dataIndex: 'active', key: 'active', render: (active) => <Tag color={active !== false ? 'green' : 'red'}>{active !== false ? 'Active' : 'Inactive'}</Tag> },
                ];
            case 'itemPrices':
                return [
                    { title: 'Item Code', dataIndex: 'itemCode', key: 'itemCode' },
                    { title: 'Item Name', dataIndex: 'itemName', key: 'itemName' },
                    { title: 'Price', dataIndex: 'price', key: 'price', render: (price) => price ? `$${price}` : '-' },
                    { title: 'Currency', dataIndex: 'currency', key: 'currency' },
                    { title: 'Effective Date', dataIndex: 'effectiveDate', key: 'effectiveDate' },
                    { title: 'Expiry Date', dataIndex: 'expiryDate', key: 'expiryDate' },
                ];
            case 'inventories':
                return [
                    { title: 'Item Code', dataIndex: 'itemCode', key: 'itemCode' },
                    { title: 'Item Name', dataIndex: 'itemName', key: 'itemName' },
                    { title: 'Quantity', dataIndex: 'quantity', key: 'quantity', render: (qty) => qty || 0 },
                    { title: 'Warehouse', dataIndex: 'warehouse', key: 'warehouse' },
                    { title: 'Location', dataIndex: 'location', key: 'location' },
                    { title: 'Last Updated', dataIndex: 'lastUpdated', key: 'lastUpdated' },
                ];
            case 'salesHeaders':
                return [
                    { title: 'Invoice No', dataIndex: 'invoiceNo', key: 'invoiceNo' },
                    { title: 'Customer Name', dataIndex: 'customerName', key: 'customerName' },
                    { title: 'Transaction Date', dataIndex: 'transactionDate', key: 'transactionDate' },
                    { title: 'Total Amount', dataIndex: 'totalAmount', key: 'totalAmount', render: (amount) => amount ? `$${amount}` : '-' },
                    { title: 'Status', dataIndex: 'status', key: 'status', render: (status) => <Tag color={status === 'completed' ? 'green' : 'orange'}>{status || 'Pending'}</Tag> },
                ];
            default:
                return [];
        }
    };

    return (
        <div className="row">
            <div className="col-md-12">
                <Card
                    title={
                        <Space>
                            <DatabaseOutlined />
                            <span>BC Data Management - <strong>{businessName || 'Select Business'}</strong></span>
                        </Space>
                    }
                    extra={
                        <Space wrap>
                            <Button 
                                type="primary" 
                                icon={<ReloadOutlined />}
                                onClick={() => fetchData(activeTab, endpoints.find(e => e.key === activeTab)?.url)}
                                loading={loading[activeTab]}
                            >
                                Fetch {endpoints.find(e => e.key === activeTab)?.label}
                            </Button>

                             <Button 
                                type="primary" 
                                icon={<CloudDownloadOutlined />}
                                onClick={pullAllData}
                                loading={pullAllLoading}
                                style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                            >
                                Fetch All Data
                            </Button>
                            <Button 
                                type="primary" 
                                icon={<FileTextOutlined />}
                                onClick={fetchSalesHeaders}
                                loading={salesHeadersLoading}
                                style={{ backgroundColor: '#faad14', borderColor: '#faad14' }}
                            >
                               POST Sales Headers
                            </Button>
                           
                        </Space>
                    }
                >
                    <Alert
                        message="BC Data Integration"
                        description="Fetch and manage your business data from the BC system. Click 'Pull All Data' to sync everything in order (Products → Shops → Customers → Item Prices → Inventories), or use individual tabs to fetch specific data types. Use the 'Sales Headers' button separately to fetch sales data."
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                    
                    {/* Progress Indicators for Pull All */}
                    {pullAllLoading && Object.keys(progress).length > 0 && (
                        <Card style={{ marginBottom: 16, backgroundColor: '#fafafa' }}>
                            <Row gutter={[16, 16]}>
                                {endpoints.map(({ key, label, icon }) => (
                                    <Col xs={12} sm={8} md={4} key={key}>
                                        <Card size="small" style={{ textAlign: 'center' }}>
                                            <Space direction="vertical" style={{ width: '100%' }}>
                                                <div style={{ fontSize: 24 }}>
                                                    {progress[key] === 'loading' && <LoadingOutlined spin style={{ color: '#1890ff' }} />}
                                                    {progress[key] === 'success' && <CheckCircleOutlined style={{ color: '#52c41a' }} />}
                                                    {progress[key] === 'error' && <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                                                </div>
                                                <div>{icon} {label}</div>
                                                {progress[key] === 'loading' && <Progress percent={100} status="active" showInfo={false} />}
                                                {progress[key] === 'success' && <Tag color="success">Completed</Tag>}
                                                {progress[key] === 'error' && <Tag color="error">Failed</Tag>}
                                            </Space>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        </Card>
                    )}

                    <Tabs 
                        activeKey={activeTab} 
                        onChange={setActiveTab}
                        type="card"
                        items={endpoints.map(({ key, label, icon }) => ({
                            key: key,
                            label: (
                                <span>
                                    {icon} {label}
                                    {!pullAllLoading && data[key]?.length > 0 && (
                                        <Tag color="blue" style={{ marginLeft: 8 }}>{data[key].length}</Tag>
                                    )}
                                </span>
                            ),
                            children: (
                                <Table
                                    columns={getColumns(key)}
                                    dataSource={data[key]}
                                    rowKey={(record) => record.id || record.code || record.productCode || record.shopCode || record.customerCode || record.itemCode || Math.random()}
                                    loading={loading[key]}
                                    scroll={{ x: true }}
                                    pagination={{ 
                                        pageSize: 10,
                                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
                                        showSizeChanger: true,
                                        showQuickJumper: true
                                    }}
                                    locale={{ emptyText: pullAllLoading ? 'Loading...' : 'No data available. Click "Fetch" to load data.' }}
                                />
                            )
                        }))}
                    />
                </Card>
            </div>

            {/* Response Message Modal - Shows only the response message, no URL */}
            <Modal
                title={
                    <Space>
                        {responseMessageModal.type === 'error' ? <CloseCircleOutlined style={{ color: '#ff4d4f' }} /> :
                         responseMessageModal.type === 'success' ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> :
                         <WarningOutlined style={{ color: '#faad14' }} />}
                        {responseMessageModal.title}
                    </Space>
                }
                open={responseMessageModal.visible}
                onOk={() => setResponseMessageModal(prev => ({ ...prev, visible: false }))}
                onCancel={() => setResponseMessageModal(prev => ({ ...prev, visible: false }))}
                width={600}
                footer={[
                    <Button key="close" type="primary" onClick={() => setResponseMessageModal(prev => ({ ...prev, visible: false }))}>
                        Close
                    </Button>
                ]}
            >
                <Paragraph>
                    <div style={{ 
                        backgroundColor: responseMessageModal.type === 'error' ? '#fff2f0' : responseMessageModal.type === 'success' ? '#f6ffed' : '#e6f7ff',
                        padding: '16px',
                        borderRadius: '8px',
                        marginTop: '8px',
                        border: `1px solid ${responseMessageModal.type === 'error' ? '#ffccc7' : responseMessageModal.type === 'success' ? '#b7eb8f' : '#91d5ff'}`
                    }}>
                        <Text style={{ fontSize: '14px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {responseMessageModal.content}
                        </Text>
                    </div>
                </Paragraph>
            </Modal>
        </div>
    );
}

export default BcDataManagement;